import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
    isPhoneNumberE164,
    ReferenceContactId,
    TaskTypeId,
    toPhoneNumberE164,
} from "@sp-crm/core";
import {
    ActivityFeedRowBody,
    ActivityRowsList,
} from "components/details-page-v-next/activity-rows";
import { EntityContactDetails } from "components/details-page-v-next/entity-contact-details";
import {
    recentActivityItemDisplay,
    sortRecentActivityItems,
} from "components/details-page-v-next/recent-activities-feed";
import { tierColorForToken } from "components/references/show-reference/tier-colors";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { ArrivingListItem, useArrivals } from "components/ui/list-motion";
import { AnimatePresence } from "framer-motion";
import { AdvancedSearchEntityType } from "generated/graphql";
import React from "react";
import { Link } from "react-router-dom";
import { contactPath } from "./agenda-actions";
import {
    agendaContactDetails,
    AgendaContactGroup,
    AgendaRecentActivityItem,
} from "./marketing-plan-agenda";
import { TouchpointRow } from "./touchpoint-row";

interface AgendaContactCardProps {
    group: AgendaContactGroup;
    hasPhoneLine: boolean;
    onSendEmail: (
        contactId: ReferenceContactId,
        activityTypeId: TaskTypeId | null,
    ) => void;
}

export const AgendaContactCard: React.FC<AgendaContactCardProps> = ({
    group,
    hasPhoneLine,
    onSendEmail,
}) => {
    const color = tierColorForToken(group.tierColor);
    const phone =
        group.contactPhone && isPhoneNumberE164(group.contactPhone)
            ? toPhoneNumberE164(group.contactPhone)
            : null;

    return (
        <div className="rounded-lg bg-white p-4 ring-1 ring-gray-300 sm:p-5">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <Link
                        to={contactPath(group.contactId)}
                        className="block truncate font-semibold text-gray-900 hover:underline">
                        {group.contactName}
                    </Link>
                    {group.orgName ? (
                        <div className="mt-0.5 truncate text-sm text-gray-500">
                            {group.orgName}
                        </div>
                    ) : null}
                </div>
                {group.tierName ? (
                    <span
                        className={`inline-flex flex-shrink-0 items-center rounded-full ${color.bg} ${color.text} px-2.5 py-0.5 text-xs font-medium`}>
                        {group.tierName}
                    </span>
                ) : null}
            </div>
            <div className="mt-2">
                <EntityContactDetails
                    entityId={group.contactId}
                    entityType={AdvancedSearchEntityType.ReferenceContact}
                    details={agendaContactDetails(group)}
                />
            </div>
            <div className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
                {group.touchpoints.map(touchpoint => (
                    <TouchpointRow
                        key={touchpoint.touchpointId}
                        touchpoint={touchpoint}
                        contactId={group.contactId}
                        contactName={group.contactName}
                        contactEmail={group.contactEmail}
                        contactEmailOptedOut={group.contactEmailOptedOut}
                        phone={phone}
                        hasPhoneLine={hasPhoneLine}
                        onSendEmail={activityTypeId =>
                            onSendEmail(group.contactId, activityTypeId)
                        }
                    />
                ))}
            </div>
            <AgendaRecentActivity items={group.recentActivity} />
        </div>
    );
};

interface AgendaRecentActivityProps {
    items: AgendaRecentActivityItem[];
}

const INITIAL_VISIBLE_COUNT = 3;
const SHOW_MORE_INCREMENT = 10;

const AgendaRecentActivity: React.FC<AgendaRecentActivityProps> = ({ items }) => {
    const [visibleCount, setVisibleCount] = React.useState(INITIAL_VISIBLE_COUNT);
    const itemIds = React.useMemo(() => items.map(item => item.id), [items]);
    const arrivals = useArrivals(itemIds);
    const sorted = sortRecentActivityItems(items);
    const visible = sorted.slice(0, visibleCount);
    const hasMore = sorted.length > visibleCount;
    const reachedEnd = !hasMore && visibleCount > INITIAL_VISIBLE_COUNT;
    return (
        <div className="mt-3 border-t border-gray-100 pt-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                Recent Activities
            </p>
            {items.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500">
                    No activity in the last 30 days.
                </p>
            ) : (
                <>
                    <ActivityRowsList className="mt-1" testId="agenda-recent-activity">
                        <AnimatePresence initial={false}>
                            {visible.map(item => (
                                <ArrivingListItem
                                    key={item.id}
                                    isNew={arrivals.has(item.id)}
                                    className="">
                                    <ActivityFeedRowBody
                                        display={recentActivityItemDisplay(item)}
                                        actions={null}
                                    />
                                </ArrivingListItem>
                            ))}
                        </AnimatePresence>
                    </ActivityRowsList>
                    {hasMore ? (
                        <InteractiveTextLink
                            className="mt-1 inline-flex items-center gap-1 text-sm font-medium"
                            onClick={() =>
                                setVisibleCount(count => count + SHOW_MORE_INCREMENT)
                            }>
                            <ChevronDownIcon aria-hidden="true" className="h-4 w-4" />
                            Show more
                        </InteractiveTextLink>
                    ) : null}
                    {reachedEnd ? (
                        <p className="mt-1 text-sm text-gray-500">
                            No more activities to show.
                        </p>
                    ) : null}
                </>
            )}
        </div>
    );
};
